import { useContext, useEffect, useState } from "react";
import { ClientConfigurationContext } from "../../../../context/ClientConfigurationContextProvider";
import { UsStateOptions } from "../../../../constants/options/UsStateOptions";
import { useFormContext } from "react-hook-form";

/**
 * Displays alerts related to the availability of states and lines of business.
 */
export default function AllowedStateAndLobAlerts() {
    const clientConfiguration = useContext(ClientConfigurationContext);
    const { watch } = useFormContext();

    const [lobStateAlert1, setLobStateAlert1] = useState(false);
    const [lobStateAlert2, setLobStateAlert2] = useState(false);
    const [lobStateAlert3, setLobStateAlert3] = useState(false);
    const [selectedStatesNotAvailableCommercial, setSelectedStatesNotAvailableCommercial] = useState([]);
    const [selectedStatesNotAvailablePersonal, setSelectedStatesNotAvailablePersonal] = useState([]);

    const watchLobPersonal = watch("lobPersonal");
    const watchLobCommercial = watch("lobCommercial");
    const watchGeicoAppointedStates = watch("geicoAppointedStates");

    useEffect(() => {
        setLobStateAlert1(false);
        setLobStateAlert2(false);
        setLobStateAlert3(false);
        setSelectedStatesNotAvailableCommercial([]);
        setSelectedStatesNotAvailablePersonal([]);

        const availableCommercialStates = clientConfiguration?.configAllowedRegistrationStatesLobs?.Commercial;
        const availablePersonalStates = clientConfiguration?.configAllowedRegistrationStatesLobs?.Personal;

        if (!availableCommercialStates || !availablePersonalStates) return;
        if (!watchGeicoAppointedStates || watchGeicoAppointedStates.length < 1) return;

        const anySelectedStatesAvailableCommercial = watchGeicoAppointedStates?.some(state => availableCommercialStates.includes(state));
        const anySelectedStatesAvailablePersonal = watchGeicoAppointedStates?.some(state => availablePersonalStates.includes(state));
        const selectedStatesNotAvailableCommercial = watchGeicoAppointedStates?.filter(state => !availableCommercialStates.includes(state));
        const selectedStatesNotAvailablePersonal = watchGeicoAppointedStates?.filter(state => !availablePersonalStates.includes(state));

        const commercialSelectedAnyStatesAvailable = watchLobCommercial && anySelectedStatesAvailableCommercial;
        const personalSelectedAnyStatesAvailable = watchLobPersonal && anySelectedStatesAvailablePersonal;

        const commercialSelectedNoStatesAvailable = watchLobCommercial && !anySelectedStatesAvailableCommercial;
        const personalSelectedNoStatesAvailable = watchLobPersonal && !anySelectedStatesAvailablePersonal;

        const commercialSelectedSomeStatesNotAvailable = watchLobCommercial && selectedStatesNotAvailableCommercial.length > 0;
        const personalSelectedSomeStatesNotAvailable = watchLobPersonal && selectedStatesNotAvailablePersonal.length > 0;

        // Both LOBs are selected and none of the states are available in either.
        setLobStateAlert1(commercialSelectedNoStatesAvailable && personalSelectedNoStatesAvailable);

        // One LOB is selected and none of the selected states are available for that LOB.
        setLobStateAlert2((watchLobCommercial != watchLobPersonal) && (commercialSelectedNoStatesAvailable || personalSelectedNoStatesAvailable));

        // Some selected state/LOB combos are available and some selected state/LOB combos are not available.
        setLobStateAlert3(
            (commercialSelectedAnyStatesAvailable || personalSelectedAnyStatesAvailable) &&
            (commercialSelectedSomeStatesNotAvailable || personalSelectedSomeStatesNotAvailable)
        );

        setSelectedStatesNotAvailableCommercial(selectedStatesNotAvailableCommercial);
        setSelectedStatesNotAvailablePersonal(selectedStatesNotAvailablePersonal);

    }, [clientConfiguration, watchLobCommercial, watchLobPersonal, watchGeicoAppointedStates]);

    function getStateNames(codes) {
        const names = codes.map(code => {
            const match = UsStateOptions.find(state => state.value == code);
            return match?.label;
        }).filter(Boolean);

        if (names.length === 0) return "";
        if (names.length === 1) return names[0];
        if (names.length === 2) return names.join(" and ");

        const allButLast = names.slice(0, -1).join(", ");
        const last = names[names.length - 1];
        return `${allButLast}, and ${last}`;
    }

    return (lobStateAlert1 || lobStateAlert2 || lobStateAlert3) &&
        <div className="container component-wrapper">
            <div className="row">
                <div className="col-sm-12">
                    <div className="alert">
                        {
                            lobStateAlert1 && <div className="alert--medium-importance">
                                <span className="icon-alert"></span>
                                <ul className="alert-list">
                                    <li className="alert-content">
                                        <button aria-label="Close alert" className="icon-close" onClick={() => setLobStateAlert1(false)}></button>
                                        <div className="h4">We're still working on getting our products in your selected state(s).</div>
                                        <p>Please select Continue below to save your profile so we can notify you when these state(s) become available.</p>
                                    </li>
                                </ul>
                            </div>
                        }

                        {
                            lobStateAlert2 && <div className="alert--medium-importance">
                                <span className="icon-alert"></span>
                                <ul className="alert-list">
                                    <li className="alert-content">
                                        <button aria-label="Close alert" className="icon-close" onClick={() => setLobStateAlert2(false)}></button>
                                        <div className="h4">We're still working on getting {watchLobPersonal && "Personal"}{watchLobCommercial && "Commercial"} in your selected state(s).</div>
                                        <p>Please select Continue below to save your profile so we can notify you when these state(s) become available.</p>
                                    </li>
                                </ul>
                            </div>
                        }
                        {
                            lobStateAlert3 &&
                            <div className="alert--medium-importance">
                                <span className="icon-alert"></span>
                                <ul className="alert-list">
                                    <li className="alert-content">
                                        <button aria-label="Close alert" className="icon-close" onClick={() => setLobStateAlert3(false)}></button>
                                        <div className="h4">We are not yet available in all of your selected states.</div>
                                        <ul style={{ listStyleType: 'circle' }}>
                                            {selectedStatesNotAvailablePersonal.length > 0 && (
                                                <li> &bull; Personal Lines not available: {getStateNames(selectedStatesNotAvailablePersonal)} </li>
                                            )}
                                            {selectedStatesNotAvailableCommercial.length > 0 && (
                                                <li> &bull; Commercial Lines not available: {getStateNames(selectedStatesNotAvailableCommercial)}</li>
                                            )}
                                        </ul>
                                    </li>
                                    <p><span className="font--bold">Please continue your application to be appointed with GEICO to sell in your other selected states. </span> We'll notify you as soon as the additional states you requested become available.</p>
                                </ul>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>;
}